import {
  deviceSearchFilter,
  systemSearchFilter,
} from 'features/Integrations/utils/integrationSearchUtils';
import { hasDevicePermission } from 'features/Integrations/features/ConnectedSystems/utils/devicePermissionUtils';
import { getDeviceTypesForSystem } from './systemStructure.selectors';
import { getOrCreate, normalizeStructureKey, sortByOrderThenName } from './systemStructure.utils';

export const buildDevicesBySystemAndDeviceType = (connectedDevices = []) => {
  const systemMap = new Map();

  connectedDevices.forEach((device) => {
    const system = device.system_type;
    const deviceType = device.device_type;

    if (!systemMap.has(system)) systemMap.set(system, new Map());
    if (!systemMap.get(system).has(deviceType)) systemMap.get(system).set(deviceType, []);

    systemMap.get(system).get(deviceType).push(device);
  });

  return systemMap;
};

const hasVisibleDevices = (devices) => devices.some((device) => deviceSearchFilter(device));

const getMatchingDevices = (connectedDevices, systemType, deviceType) =>
  (connectedDevices || []).filter(
    (device) =>
      normalizeStructureKey(device?.system_type) === normalizeStructureKey(systemType) &&
      normalizeStructureKey(device?.device_type) === normalizeStructureKey(deviceType) &&
      deviceSearchFilter(device),
  );

const getCategoryMeta = (structure, deviceTypeMeta, system) =>
  structure?.categories?.find(
    (category) => category.id === deviceTypeMeta.structure_category_id,
  ) || {
    id: system.system_type,
    name: system.system_type,
  };

const getTypeMeta = (categoryMeta, deviceTypeMeta) =>
  categoryMeta?.types?.find((type) => type.id === deviceTypeMeta.structure_type_id) || {
    id: deviceTypeMeta.name,
    name: deviceTypeMeta.name,
  };

const appendDeviceGroup = ({ category, type, systemNode, systemKey, deviceType, devices }) => {
  systemNode.deviceGroups.push({
    id: `${systemKey}::${deviceType}`,
    deviceType,
    devices,
  });
  systemNode.devicesCount += devices.length;
  type.devicesCount += devices.length;
  category.devicesCount += devices.length;
};

const addDeviceTypeToHierarchy = ({ categoryMap, structure, system, deviceTypeMeta, devices }) => {
  const categoryMeta = getCategoryMeta(structure, deviceTypeMeta, system);
  const typeMeta = getTypeMeta(categoryMeta, deviceTypeMeta);
  const fallbackSystemName = system.system_name || system.system_type || 'Unknown System';
  const systemName = deviceTypeMeta.system_display_name || fallbackSystemName;

  const category = getOrCreate(categoryMap, categoryMeta.id || categoryMeta.name, () => ({
    id: categoryMeta.id || categoryMeta.name,
    name: categoryMeta.name,
    sort_order: categoryMeta.sort_order ?? 999,
    types: new Map(),
    systemsCount: 0,
    devicesCount: 0,
  }));

  const type = getOrCreate(category.types, typeMeta.id || typeMeta.name, () => ({
    id: `${category.id}::${typeMeta.id || typeMeta.name}`,
    name: typeMeta.name,
    sort_order: typeMeta.sort_order ?? 999,
    systems: new Map(),
    devicesCount: 0,
  }));

  const systemKey = `${system.id || system.system_type}::${systemName}`;
  const systemNode = getOrCreate(type.systems, systemKey, () => ({
    id: systemKey,
    name: systemName,
    sourceSystem: system,
    deviceGroups: [],
    devicesCount: 0,
  }));

  appendDeviceGroup({
    category,
    type,
    systemNode,
    systemKey,
    deviceType: deviceTypeMeta.name,
    devices,
  });
};

const toHierarchyList = (categoryMap) =>
  Array.from(categoryMap.values())
    .sort(sortByOrderThenName)
    .map((category) => {
      const types = Array.from(category.types.values())
        .sort(sortByOrderThenName)
        .map((type) => {
          const systems = Array.from(type.systems.values());
          return {
            ...type,
            systems,
            systemsCount: systems.length,
          };
        });

      return {
        ...category,
        types,
        systemsCount: types.reduce((count, type) => count + type.systemsCount, 0),
      };
    });

export const buildConnectedSystemsHierarchy = ({
  structure,
  integrations = [],
  connectedDevices = [],
  deviceTypes = {},
  perms,
  canViewUSGS = true,
}) => {
  const categoryMap = new Map();

  (integrations || []).forEach((system) => {
    if (!system) return;
    if (system.system_type === 'USGS' && !canViewUSGS) return;
    if (!systemSearchFilter(system, deviceTypes, connectedDevices)) return;

    getDeviceTypesForSystem(structure, system.system_type).forEach((deviceTypeMeta) => {
      const deviceType = deviceTypeMeta.name;
      if (!hasDevicePermission(perms, system.system_type, deviceType, 'view')) return;

      const devices = getMatchingDevices(connectedDevices, system.system_type, deviceType);
      if (
        !hasVisibleDevices(devices) &&
        !systemSearchFilter(system, deviceTypes, connectedDevices)
      ) {
        return;
      }

      addDeviceTypeToHierarchy({
        categoryMap,
        structure,
        system,
        deviceTypeMeta,
        devices,
      });
    });
  });

  return toHierarchyList(categoryMap);
};
